import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, Check, ChevronDown, LogOut, Menu, Plus, Settings, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";

export const OrgSwitcher = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizations, currentOrg, switchOrganization } = useAuth();
  if (!currentOrg) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-9 max-w-[260px] gap-2 bg-card px-2.5 font-medium" data-testid="topbar-org-switcher">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent text-[11px] font-semibold text-foreground">{initials(currentOrg.name)}</span>
          <span className="truncate">{currentOrg.name}</span>
          {currentOrg.is_demo && <Badge variant="outline" className="hidden border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] uppercase text-amber-950 sm:inline-flex">{t("admin.demo")}</Badge>}
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 bg-card">
        <DropdownMenuLabel className="og-section-label">{t("nav.switchOrg")}</DropdownMenuLabel>
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            data-testid={`org-switcher-item-${org.id}`}
            className="cursor-pointer gap-2"
            onSelect={() => {
              if (org.id !== currentOrg.id) {
                switchOrganization(org.id);
                navigate(org.onboarding_completed ? "/app" : "/onboarding");
              }
            }}
          >
            <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="flex-1 truncate">{org.name}</span>
            <span className="text-[11px] text-muted-foreground">{t(`settings.roles.${org.role}`)}</span>
            {org.id === currentOrg.id && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer gap-2" onSelect={() => navigate("/onboarding?new=1")} data-testid="org-switcher-new">
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("nav.newOrg")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const UserMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, currentOrg, isPlatformAdmin, logout } = useAuth();
  if (!user) return null;
  const onLogout = async () => {
    await logout();
    toast.info(t("auth.loggedOut"));
    navigate("/login", { replace: true });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="og-focus flex h-9 items-center gap-2 rounded-full pl-1 pr-2 transition-colors duration-150 hover:bg-muted" data-testid="topbar-user-menu" aria-label={t("nav.account")}>
          <Avatar className="h-7 w-7">
            <AvatarFallback className="bg-slate-900 text-[11px] font-semibold text-white">{initials(user.full_name)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-[140px] truncate text-sm font-medium md:inline">{user.full_name}</span>
          <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:inline" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-card">
        <DropdownMenuLabel>
          <div className="truncate text-sm font-semibold">{user.full_name}</div>
          <div className="truncate text-xs font-normal text-muted-foreground" data-testid="user-menu-email">{user.email}</div>
          {currentOrg && <div className="mt-1 text-[11px] text-muted-foreground">{t("settings.yourRole")} : {t(`settings.roles.${currentOrg.role}`)}</div>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isPlatformAdmin && (
          <>
            <DropdownMenuItem className="cursor-pointer gap-2" onSelect={() => navigate("/app/admin")} data-testid="user-menu-platform-admin">
              <ShieldCheck className="h-4 w-4" />OneGate Administration
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem className="cursor-pointer gap-2" onSelect={() => navigate("/app/settings?tab=profile")} data-testid="user-menu-profile">
          <UserRound className="h-4 w-4" aria-hidden="true" />{t("nav.profile")}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer gap-2" onSelect={() => navigate("/app/settings?tab=security")} data-testid="user-menu-security">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />{t("settings.tabs.security")}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer gap-2" onSelect={() => navigate("/app/settings")} data-testid="user-menu-settings">
          <Settings className="h-4 w-4" aria-hidden="true" />{t("nav.settings")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer gap-2 text-rose-900 focus:bg-rose-50 focus:text-rose-900" onSelect={onLogout} data-testid="logout-button">
          <LogOut className="h-4 w-4" aria-hidden="true" />{t("nav.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Topbar = ({ onOpenMobileMenu, className = "" }) => {
  const { t } = useTranslation();
  return (
    <header className={cn("sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-card px-3 sm:px-4 lg:px-6", className)} data-testid="app-topbar">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onOpenMobileMenu} aria-label={t("nav.openMenu")} data-testid="topbar-mobile-menu-button">
        <Menu className="h-5 w-5" />
      </Button>
      <OrgSwitcher />
      <div className="ml-auto flex items-center gap-1">
        <UserMenu />
      </div>
    </header>
  );
};
